import {
  EXPENSE_CATEGORY_LABELS,
  type Equipment,
  Equipment as EquipmentSchema,
  type Expense,
  type ExpenseCategory,
  type Installment,
  PURCHASE_PAYMENT_LABELS,
  type RecurringExpense,
  RecurringExpense as RecurringSchema,
  addMonths,
  formatBRL,
  formatIsoDate,
  isoDateInSaoPaulo,
  monthLabel,
  monthOf,
} from '@deizi/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { PageHead } from '../../components/PageHead';
import { Drawer } from '../../components/Overlays';
import { EmptyState, ErrorBox, Skeleton } from '../../components/States';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import {
  useEquipment,
  useExpenses,
  useInvalidateExpenses,
  useRecurring,
} from '../../lib/expenses-api';
import { errorMessage } from '../../lib/messages';
import { useIsAdmin } from '../../lib/session';
import { useIsPhone } from '../../lib/use-media';
import {
  AttachDialog,
  DeleteDialog,
  EquipmentDialog,
  ExpenseDialog,
  LaunchDialog,
  PlanDialog,
  RecurringDialog,
  installmentsText,
} from './dialogs';
import { ExportCsvButton } from '../../components/ExportCsv';

type Tab = 'despesas' | 'recorrentes' | 'equipamentos';
type Dialog =
  | { kind: 'new' }
  | { kind: 'edit' | 'attach' | 'launch' | 'delete'; expense: Expense }
  | { kind: 'recurring'; recurring?: RecurringExpense }
  | { kind: 'equipment'; equipment?: Equipment }
  | { kind: 'plan'; equipment: Equipment }
  | null;

const h = (n: number) => `${n.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} h`;
const dm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

function subline(e: Expense): string {
  const parts: string[] = [];
  if (e.recurring) parts.push(e.status === 'pending' ? 'recorrente · a lançar' : 'recorrente');
  else if (e.status === 'pending') parts.push('a lançar');
  if (e.asset) parts.push('ativo · fora do total');
  if (e.purchase) parts.push('da compra');
  if (e.maintenance) parts.push('da manutenção');
  if (e.supplier && !e.description.includes(e.supplier)) parts.push(e.supplier);
  return parts.join(' · ');
}

const valueText = (e: Expense) =>
  e.amountCentavos === null ? 'a lançar' : formatBRL(e.amountCentavos);

export function ExpensesPage({ startNew = false }: { startNew?: boolean }) {
  const params = useParams();
  const tab: Tab =
    params.tab === 'recorrentes' || params.tab === 'equipamentos' ? params.tab : 'despesas';
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const phone = useIsPhone();
  const [month, setMonth] = useState(monthOf());
  const [dialog, setDialogState] = useState<Dialog>(startNew ? { kind: 'new' } : null);
  const setDialog = (d: Dialog) => {
    setDialogState(d);
    if (!d && startNew) void navigate('/despesas', { replace: true });
  };
  const list = useExpenses(month);
  const recurring = useRecurring();
  const equipment = useEquipment();
  const current = monthOf();
  const months = Array.from({ length: 13 }, (_, i) => addMonths(current, 1 - i));

  return (
    <>
      <PageHead
        crumb="Dinheiro"
        title="Despesas"
        actions={
          <div className="row wrap" style={{ gap: 10 }}>
            {tab === 'despesas' ? (
              <>
                {phone ? null : <ExportCsvButton dataset="despesas" params={{ month }} />}
                <select
                  className="input"
                  aria-label="Mês"
                  style={{ width: 180 }}
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                >
                  {months.map((m) => (
                    <option key={m} value={m}>
                      {monthLabel(m)}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn p"
                  onClick={() =>
                    phone ? void navigate('/despesas/nova') : setDialog({ kind: 'new' })
                  }
                >
                  + Despesa
                </button>
              </>
            ) : null}
            {tab === 'recorrentes' ? (
              <button
                type="button"
                className="btn p"
                onClick={() => setDialog({ kind: 'recurring' })}
              >
                + Recorrente
              </button>
            ) : null}
            {tab === 'equipamentos' && isAdmin ? (
              <button
                type="button"
                className="btn p"
                onClick={() => setDialog({ kind: 'equipment' })}
              >
                + Equipamento
              </button>
            ) : null}
          </div>
        }
      />
      <nav className="tabs" aria-label="Seções de despesas">
        <Link to="/despesas" className={tab === 'despesas' ? 'on' : ''}>
          Despesas <span className="xs muted">{list.data?.data.length ?? ''}</span>
        </Link>
        <Link to="/despesas/recorrentes" className={tab === 'recorrentes' ? 'on' : ''}>
          Recorrentes <span className="xs muted">{recurring.data?.data.length ?? ''}</span>
        </Link>
        <Link to="/despesas/equipamentos" className={tab === 'equipamentos' ? 'on' : ''}>
          Equipamentos <span className="xs muted">{equipment.data?.data.length ?? ''}</span>
        </Link>
      </nav>
      {tab === 'despesas' ? <ExpensesTab month={month} onDialog={setDialog} /> : null}
      {tab === 'recorrentes' ? <RecurringTab onDialog={setDialog} /> : null}
      {tab === 'equipamentos' ? <EquipmentTab onDialog={setDialog} /> : null}
      <Dialogs dialog={dialog} onClose={() => setDialog(null)} />
    </>
  );
}

function Dialogs({ dialog, onClose }: { dialog: Dialog; onClose: () => void }) {
  if (!dialog) return null;
  switch (dialog.kind) {
    case 'new':
      return <ExpenseDialog onClose={onClose} />;
    case 'edit':
      return <ExpenseDialog expense={dialog.expense} onClose={onClose} />;
    case 'attach':
      return <AttachDialog expense={dialog.expense} onClose={onClose} />;
    case 'launch':
      return <LaunchDialog expense={dialog.expense} onClose={onClose} />;
    case 'delete':
      return <DeleteDialog expense={dialog.expense} onClose={onClose} />;
    case 'recurring':
      return <RecurringDialog recurring={dialog.recurring} onClose={onClose} />;
    case 'equipment':
      return <EquipmentDialog equipment={dialog.equipment} onClose={onClose} />;
    case 'plan':
      return <PlanDialog equipment={dialog.equipment} onClose={onClose} />;
  }
}

// ---- Despesas tab -------------------------------------------------------------------------------

const COLS = '64px minmax(0,2.4fr) 130px 120px 110px';

function ExpensesTab({ month, onDialog }: { month: string; onDialog: (d: Dialog) => void }) {
  const phone = useIsPhone();
  const list = useExpenses(month);
  const [category, setCategory] = useState<ExpenseCategory | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const name = monthLabel(month, { year: false });
  if (list.isPending) return <Skeleton rows={5} height={48} />;
  if (list.isError)
    return (
      <div className="card">
        <ErrorBox
          title="Não foi possível carregar as despesas"
          onRetry={() => void list.refetch()}
        />
      </div>
    );
  const { data, totals, upcoming } = list.data;
  const open = data.find((e) => e.id === openId) ?? null;
  if (data.length === 0)
    return (
      <div className="card">
        <EmptyState
          title={`Nenhuma despesa em ${name} ainda`}
          action={
            <button type="button" className="btn p" onClick={() => onDialog({ kind: 'new' })}>
              Lançar despesa
            </button>
          }
        >
          Lance compras de filamento, anúncios e impostos para o relatório mostrar o lucro de
          verdade.
        </EmptyState>
      </div>
    );
  const counts = new Map<ExpenseCategory, number>();
  for (const e of data) counts.set(e.category, (counts.get(e.category) ?? 0) + 1);
  const rows = category ? data.filter((e) => e.category === category) : data;
  const shownTotal = category
    ? (totals.byCategory.find((b) => b.category === category)?.totalCentavos ?? 0)
    : totals.totalCentavos;
  const notes = [
    totals.assetCount
      ? `${totals.assetCount} ${totals.assetCount === 1 ? 'ativo' : 'ativos'} fora do total (${formatBRL(totals.assetCentavos)})`
      : null,
    totals.pendingCount ? `${totals.pendingCount} a lançar` : null,
  ].filter(Boolean);
  const maxCat = Math.max(1, ...totals.byCategory.map((b) => b.totalCentavos));

  return (
    <div className="col" style={{ gap: 18 }}>
      <div className="row wrap" role="group" aria-label="Filtrar por categoria" style={{ gap: 8 }}>
        <button
          type="button"
          className={category === null ? 'chip on' : 'chip'}
          aria-pressed={category === null}
          onClick={() => setCategory(null)}
        >
          Todas{' '}
          <span className="xs" style={{ opacity: 0.75 }}>
            {data.length}
          </span>
        </button>
        {[...counts].map(([c, n]) => (
          <button
            key={c}
            type="button"
            className={category === c ? 'chip on' : 'chip'}
            aria-pressed={category === c}
            onClick={() => setCategory(category === c ? null : c)}
          >
            {EXPENSE_CATEGORY_LABELS[c]}{' '}
            <span className="xs" style={{ opacity: 0.75 }}>
              {n}
            </span>
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'flex-start' }}>
        <section className="card flat" style={{ flex: '3 1 640px', minWidth: 0 }}>
          <div className="row between" style={{ padding: '16px 18px 6px', alignItems: 'flex-end' }}>
            <div>
              <h2>{monthLabel(month, { year: false })}</h2>
              <p className="xs muted">{notes.length ? notes.join(' · ') : 'tudo lançado'}</p>
            </div>
            <span className="fr tab" style={{ fontSize: 20 }}>
              {formatBRL(shownTotal)}
            </span>
          </div>
          {phone ? (
            <div className="gt">
              {rows.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  className="gtr"
                  style={{
                    gridTemplateColumns: '1fr auto',
                    textAlign: 'left',
                    background: e.status === 'pending' ? 'var(--warn-bg)' : undefined,
                  }}
                  onClick={() => setOpenId(e.id)}
                >
                  <span style={{ minWidth: 0 }}>
                    <b style={{ fontWeight: 500 }}>{e.description}</b>
                    <span className="xs muted" style={{ display: 'block' }}>
                      {dm(e.spentOn)} · {EXPENSE_CATEGORY_LABELS[e.category]}
                      {subline(e) ? ` · ${subline(e)}` : ''}
                    </span>
                  </span>
                  <span className={e.asset || e.status === 'pending' ? 'num muted' : 'num'}>
                    {valueText(e)}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="gt" style={{ overflowX: 'auto' }}>
              <div
                className="gtr head"
                style={{ gridTemplateColumns: `${COLS} 120px`, minWidth: 700 }}
              >
                <span>Data</span>
                <span>Descrição</span>
                <span>Categoria</span>
                <span>Pagamento</span>
                <span className="num">Valor</span>
                <span />
              </div>
              {rows.map((e) => (
                <div
                  key={e.id}
                  className="gtr"
                  style={{
                    gridTemplateColumns: `${COLS} 120px`,
                    minWidth: 700,
                    padding: 0,
                    background:
                      e.id === openId
                        ? 'var(--pe-soft)'
                        : e.status === 'pending'
                          ? 'var(--warn-bg)'
                          : undefined,
                  }}
                >
                  <button
                    type="button"
                    aria-label={`Ver detalhes: ${e.description}`}
                    onClick={() => setOpenId(e.id)}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: COLS,
                      gridColumn: '1 / 6',
                      gap: 12,
                      alignItems: 'center',
                      padding: '12px 16px',
                      border: 0,
                      background: 'transparent',
                      font: 'inherit',
                      color: 'inherit',
                      textAlign: 'left',
                      cursor: 'pointer',
                      minHeight: 56,
                    }}
                  >
                    <span className="tab sm">{dm(e.spentOn)}</span>
                    <span style={{ minWidth: 0 }}>
                      <span
                        style={{
                          display: 'block',
                          fontWeight: 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {e.description}
                      </span>
                      {subline(e) ? <span className="xs muted">{subline(e)}</span> : null}
                    </span>
                    <span>
                      <span className="badge">{EXPENSE_CATEGORY_LABELS[e.category]}</span>
                    </span>
                    <span className="sm">
                      {e.paymentMethod ? PURCHASE_PAYMENT_LABELS[e.paymentMethod] : '—'}
                    </span>
                    <span className={e.asset || e.status === 'pending' ? 'num muted' : 'num'}>
                      {valueText(e)}
                    </span>
                  </button>
                  <span className="row" style={{ justifyContent: 'flex-end', paddingRight: 12 }}>
                    {e.status === 'pending' ? (
                      <button
                        type="button"
                        className="btn sm"
                        onClick={() => onDialog({ kind: 'launch', expense: e })}
                      >
                        Lançar
                      </button>
                    ) : e.files.length ? (
                      <span className="badge ok" title="Comprovante anexado">
                        ✓ recibo
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="btn sm g"
                        onClick={() => onDialog({ kind: 'attach', expense: e })}
                      >
                        Anexar
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
          {rows.length === 0 ? (
            <div className="col" style={{ alignItems: 'center', gap: 8, padding: '28px 16px' }}>
              <p className="muted sm">
                Nenhuma despesa de {category ? EXPENSE_CATEGORY_LABELS[category] : ''} em {name}.
              </p>
              <button type="button" className="btn sm" onClick={() => setCategory(null)}>
                Limpar filtros
              </button>
            </div>
          ) : null}
        </section>
        <aside className="col" style={{ flex: '2 1 300px', minWidth: 0, gap: 18 }}>
          <section className="card col" style={{ gap: 10 }}>
            <h3>Por categoria · {name}</h3>
            {totals.byCategory.length === 0 ? (
              <p className="sm muted">Nada lançado ainda.</p>
            ) : null}
            {totals.byCategory.map((b) => (
              <button
                key={b.category}
                type="button"
                className="col"
                aria-label={`Filtrar ${EXPENSE_CATEGORY_LABELS[b.category]}`}
                onClick={() => setCategory(b.category)}
                style={{
                  gap: 4,
                  border: 0,
                  background: 'transparent',
                  padding: '4px 0',
                  font: 'inherit',
                  color: 'inherit',
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
              >
                <span className="row between sm">
                  <span>{EXPENSE_CATEGORY_LABELS[b.category]}</span>
                  <span className="tab" style={{ fontWeight: 500 }}>
                    {formatBRL(b.totalCentavos)}
                  </span>
                </span>
                <span
                  style={{
                    display: 'block',
                    height: 8,
                    borderRadius: 4,
                    background: 'var(--co)',
                    width: `${Math.max(3, Math.round((b.totalCentavos / maxCat) * 100))}%`,
                  }}
                />
              </button>
            ))}
            <p className="xs muted">
              Ativos (equipamentos) ficam fora do total: entram como depreciação ou custo mensal.
            </p>
          </section>
          <section className="card col" style={{ gap: 8 }}>
            <div className="row between">
              <h3>Próximas saídas</h3>
              <Link className="btn sm g" to="/despesas/equipamentos">
                Equipamentos
              </Link>
            </div>
            {upcoming.length === 0 ? (
              <p className="sm muted">Nenhuma parcela ou conta nos próximos dias.</p>
            ) : null}
            {upcoming.map((u) => (
              <div key={`${u.refId}-${u.dueOn}`} className="kv sm">
                <span>
                  {u.label}
                  <span className="xs muted" style={{ display: 'block' }}>
                    {formatIsoDate(u.dueOn)}
                  </span>
                </span>
                <span className="v">
                  {u.amountCentavos === null ? 'valor a confirmar' : formatBRL(u.amountCentavos)}
                </span>
              </div>
            ))}
          </section>
        </aside>
      </div>
      {open ? (
        <ExpenseDrawer expense={open} onClose={() => setOpenId(null)} onDialog={onDialog} />
      ) : null}
    </div>
  );
}

function ExpenseDrawer({
  expense: e,
  onClose,
  onDialog,
}: {
  expense: Expense;
  onClose: () => void;
  onDialog: (d: Dialog) => void;
}) {
  const go = (d: Dialog) => {
    onClose();
    onDialog(d);
  };
  return (
    <Drawer
      kicker={`${formatIsoDate(e.spentOn)} · ${EXPENSE_CATEGORY_LABELS[e.category]}`}
      title={e.description}
      onClose={onClose}
    >
      <div className="col" style={{ gap: 14 }}>
        <span className="fr tab" style={{ fontSize: 28 }}>
          {e.amountCentavos === null ? 'a lançar' : formatBRL(e.amountCentavos)}
        </span>
        {e.asset ? (
          <div className="alert info">
            Lançada como equipamento: fica fora do total do mês e entra como custo mensal.
          </div>
        ) : null}
        {e.locked ? <p className="xs muted">{e.locked}</p> : null}
        <div className="col" style={{ gap: 2 }}>
          <div className="kv sm">
            <span className="muted">Pagamento</span>
            <span className="v">
              {e.paymentMethod ? PURCHASE_PAYMENT_LABELS[e.paymentMethod] : '—'}
            </span>
          </div>
          <div className="kv sm">
            <span className="muted">Fornecedor</span>
            <span className="v">{e.supplier ?? '—'}</span>
          </div>
          <div className="kv sm">
            <span className="muted">Recorrente</span>
            <span className="v">{e.recurring ? 'sim' : 'não'}</span>
          </div>
          <div className="kv sm">
            <span className="muted">Custos fixos do mês</span>
            <span className="v">
              {e.asset
                ? 'só a depreciação mensal'
                : e.fixedCost
                  ? 'entra'
                  : 'não entra (já está no custo das peças)'}
            </span>
          </div>
          {e.maintenance ? (
            <div className="kv sm">
              <span className="muted">Impressora</span>
              <span className="v">{e.maintenance.printerName}</span>
            </div>
          ) : null}
          <div className="kv sm">
            <span className="muted">Notas</span>
            <span className="v">{e.notes ?? '—'}</span>
          </div>
        </div>
        <span className="lbl">Recibo</span>
        {e.files.length ? (
          <div className="col" style={{ gap: 8 }}>
            {e.files.map((f) =>
              f.mime.startsWith('image/') ? (
                <a key={f.id} href={f.url} target="_blank" rel="noreferrer">
                  <img
                    src={f.url}
                    alt={`Recibo ${f.name}`}
                    style={{ width: '100%', borderRadius: 12, maxHeight: 260, objectFit: 'cover' }}
                  />
                </a>
              ) : (
                <a key={f.id} className="btn sm" href={f.url} target="_blank" rel="noreferrer">
                  {f.name}
                </a>
              ),
            )}
          </div>
        ) : (
          <div className="dropzone" style={{ padding: 18 }}>
            <span className="sm">Sem recibo anexado.</span>
            <button
              type="button"
              className="btn sm"
              onClick={() => go({ kind: 'attach', expense: e })}
            >
              Anexar recibo
            </button>
          </div>
        )}
        <div className="row wrap" style={{ gap: 8 }}>
          {e.status === 'pending' ? (
            <button
              type="button"
              className="btn p"
              onClick={() => go({ kind: 'launch', expense: e })}
            >
              Lançar valor
            </button>
          ) : null}
          {e.status === 'paid' ? (
            <button type="button" className="btn" onClick={() => go({ kind: 'edit', expense: e })}>
              Editar
            </button>
          ) : null}
          {e.files.length ? (
            <button
              type="button"
              className="btn g"
              onClick={() => go({ kind: 'attach', expense: e })}
            >
              Anexar outro
            </button>
          ) : null}
          {e.locked ? null : (
            <button
              type="button"
              className="btn d"
              onClick={() => go({ kind: 'delete', expense: e })}
            >
              Excluir
            </button>
          )}
        </div>
      </div>
    </Drawer>
  );
}

// ---- Recorrentes tab ----------------------------------------------------------------------------

function RecurringTab({ onDialog }: { onDialog: (d: Dialog) => void }) {
  const toast = useToast();
  const invalidate = useInvalidateExpenses();
  const list = useRecurring();
  const toggle = useMutation({
    mutationFn: (r: RecurringExpense) =>
      api.patch(`/recurring-expenses/${r.id}`, RecurringSchema, { active: !r.active }),
    onSuccess: (r) => {
      invalidate();
      toast(r.active ? `${r.name} ativada` : `${r.name} pausada: não gera mais lançamentos`);
    },
    onError: (err) => toast(errorMessage(err)),
  });
  if (list.isPending) return <Skeleton rows={3} height={48} />;
  if (list.isError)
    return (
      <div className="card">
        <ErrorBox
          title="Não foi possível carregar as recorrentes"
          onRetry={() => void list.refetch()}
        />
      </div>
    );
  const rows = list.data.data;
  const active = rows.filter((r) => r.active);
  const fixed = active.reduce((n, r) => n + (r.amountCentavos ?? 0), 0);
  const variable = active.filter((r) => r.amountCentavos === null).length;
  const cols = '2fr 1fr 1fr 1fr 1fr 70px';
  return (
    <section className="card flat">
      <div className="row between" style={{ padding: '16px 18px 6px' }}>
        <div>
          <h2>Recorrentes</h2>
          <p className="xs muted">Viram despesa “a lançar” no dia; você confirma o valor.</p>
        </div>
        <span className="sm muted">
          {formatBRL(fixed)}/mês
          {variable ? ` + ${variable} variável${variable > 1 ? 'is' : ''}` : ''}
        </span>
      </div>
      {rows.length === 0 ? (
        <EmptyState
          title="Nenhuma recorrente"
          action={
            <button type="button" className="btn p" onClick={() => onDialog({ kind: 'recurring' })}>
              + Recorrente
            </button>
          }
        >
          DAS, internet, assinatura do fatiador: viram “a lançar” todo mês no dia certo.
        </EmptyState>
      ) : (
        <div className="gt" style={{ overflowX: 'auto' }}>
          <div className="gtr head" style={{ gridTemplateColumns: cols, minWidth: 620 }}>
            <span>Nome</span>
            <span>Categoria</span>
            <span>Quando</span>
            <span>Próximo</span>
            <span className="num">Valor</span>
            <span>Ativa</span>
          </div>
          {rows.map((r) => (
            <div
              key={r.id}
              className="gtr"
              style={{ gridTemplateColumns: cols, minWidth: 620, opacity: r.active ? 1 : 0.55 }}
            >
              <button
                type="button"
                className="btn g sm"
                style={{ justifyContent: 'flex-start', fontWeight: 500, padding: 0 }}
                onClick={() => onDialog({ kind: 'recurring', recurring: r })}
              >
                {r.name}
              </button>
              <span>
                <span className="badge">{EXPENSE_CATEGORY_LABELS[r.category]}</span>
              </span>
              <span className="sm">todo dia {r.day}</span>
              <span className="sm tab">{r.nextOn ? formatIsoDate(r.nextOn) : 'pausada'}</span>
              <span className={r.amountCentavos === null ? 'num muted' : 'num'}>
                {r.amountCentavos === null ? 'variável' : formatBRL(r.amountCentavos)}
              </span>
              <button
                type="button"
                className={r.active ? 'toggle on' : 'toggle'}
                role="switch"
                aria-checked={r.active}
                aria-label={`${r.name} ativa`}
                disabled={toggle.isPending}
                onClick={() => toggle.mutate(r)}
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ---- Equipamentos tab ---------------------------------------------------------------------------

function installmentBadge(i: Installment, today: string): { text: string; cls: string } {
  if (i.paidAt) return { text: 'paga', cls: 'badge ok' };
  if (i.dueOn < today) return { text: 'atrasada', cls: 'badge err' };
  const days = Math.round(
    (new Date(`${i.dueOn}T12:00:00Z`).getTime() - new Date(`${today}T12:00:00Z`).getTime()) /
      86_400_000,
  );
  if (days === 0) return { text: 'vence hoje', cls: 'badge warn' };
  if (days <= 15)
    return { text: `vence em ${days} ${days === 1 ? 'dia' : 'dias'}`, cls: 'badge warn' };
  return { text: 'a vencer', cls: 'badge' };
}

function Installments({
  equipment: e,
  onDialog,
}: {
  equipment: Equipment;
  onDialog: (d: Dialog) => void;
}) {
  const toast = useToast();
  const invalidate = useInvalidateExpenses();
  const isAdmin = useIsAdmin();
  const [all, setAll] = useState(false);
  const today = isoDateInSaoPaulo();
  const pay = useMutation({
    mutationFn: (v: { i: Installment; action: 'pay' | 'unpay' }) =>
      api.post(`/equipment-installments/${v.i.id}/${v.action}`, EquipmentSchema),
    onSuccess: (_e, v) => {
      invalidate();
      if (v.action === 'pay')
        toast(
          `Parcela ${v.i.number}/${v.i.count} marcada como paga · ${formatBRL(v.i.amountCentavos)}`,
        );
    },
    onError: (err) => toast(errorMessage(err)),
  });
  const list = e.installments;
  if (list.length === 0)
    return (
      <div className="row between">
        <span className="sm muted">À vista, sem parcelas.</span>
        {isAdmin ? (
          <button
            type="button"
            className="btn sm g"
            onClick={() => onDialog({ kind: 'plan', equipment: e })}
          >
            Plano de parcelas
          </button>
        ) : null}
      </div>
    );
  const paidCount = list.filter((i) => i.paidAt).length;
  const nextIndex = Math.max(
    0,
    list.findIndex((i) => !i.paidAt),
  );
  const shown = all ? list : list.slice(Math.max(0, nextIndex - 1), Math.max(0, nextIndex - 1) + 3);
  const same = list.every((i) => i.amountCentavos === list[0]!.amountCentavos);
  return (
    <div className="col" style={{ gap: 6 }}>
      <div className="row between">
        <span className="lbl">
          Parcelas · {list.length} ×{' '}
          {same ? formatBRL(list[0]!.amountCentavos) : 'valores diferentes'}
        </span>
        <span className="sm muted">
          {paidCount} de {list.length} pagas · {formatBRL(e.unpaidCentavos)} a pagar
        </span>
      </div>
      <div className="col" style={{ gap: 0 }}>
        {shown.map((i) => {
          const b = installmentBadge(i, today);
          return (
            <div key={i.id} className="kv sm" style={{ alignItems: 'center' }}>
              <span className="tab">
                {i.number}/{i.count} · {formatIsoDate(i.dueOn)}
                {same ? '' : ` · ${formatBRL(i.amountCentavos)}`}
              </span>
              <span className="row" style={{ gap: 10 }}>
                <span className={b.cls}>{b.text}</span>
                <button
                  type="button"
                  className="btn sm g"
                  disabled={pay.isPending}
                  onClick={() => pay.mutate({ i, action: i.paidAt ? 'unpay' : 'pay' })}
                >
                  {i.paidAt ? 'Desfazer' : 'Marcar como paga'}
                </button>
              </span>
            </div>
          );
        })}
      </div>
      <div className="row wrap between" style={{ gap: 8 }}>
        {list.length > 3 ? (
          <button
            type="button"
            className="btn sm g"
            style={{ padding: 0 }}
            aria-expanded={all}
            onClick={() => setAll(!all)}
          >
            {all
              ? 'Mostrar só as próximas'
              : `Ver as ${list.length} parcelas, até ${formatIsoDate(list.at(-1)!.dueOn)}`}
          </button>
        ) : (
          <span />
        )}
        {isAdmin && paidCount === 0 ? (
          <button
            type="button"
            className="btn sm g"
            onClick={() => onDialog({ kind: 'plan', equipment: e })}
          >
            Refazer plano
          </button>
        ) : null}
      </div>
    </div>
  );
}

function EquipmentTab({ onDialog }: { onDialog: (d: Dialog) => void }) {
  const list = useEquipment();
  const isAdmin = useIsAdmin();
  if (list.isPending) return <Skeleton rows={2} height={140} />;
  if (list.isError)
    return (
      <div className="card">
        <ErrorBox
          title="Não foi possível carregar os equipamentos"
          onRetry={() => void list.refetch()}
        />
      </div>
    );
  const printers = list.data.data.filter((e) => e.kind === 'printer');
  const others = list.data.data.filter((e) => e.kind === 'other');
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'flex-start' }}>
      <div className="col" style={{ flex: '3 1 540px', minWidth: 0, gap: 18 }}>
        {printers.length === 0 ? (
          <div className="card">
            <EmptyState title="Nenhuma impressora">
              Cadastre a impressora em Impressoras: ela aparece aqui com a depreciação.
            </EmptyState>
          </div>
        ) : null}
        {printers.map((e) => {
          const p = e.printer!;
          return (
            <section key={e.id} className="card col" style={{ gap: 12 }}>
              <div className="row between">
                <h2>
                  {e.name}
                  {p.retired ? (
                    <span className="badge" style={{ marginLeft: 8 }}>
                      aposentada
                    </span>
                  ) : null}
                </h2>
                <span className="tab fr" style={{ fontSize: 20 }}>
                  {formatBRL(e.valueCentavos)}
                </span>
              </div>
              <p className="sm muted">
                Ativo, não despesa: entra no preço como depreciação de{' '}
                {formatBRL(Math.round(p.depreciationPerHourCentavos))} por hora impressa (vida útil
                de {h(p.lifetimeHours)}).
              </p>
              <div className="card sunk col" style={{ gap: 8, padding: 14 }}>
                <div className="kv sm">
                  <span className="muted">Depreciado até hoje</span>
                  <span className="v">
                    {formatBRL(e.depreciatedCentavos)} · {h(p.hoursPrinted)}
                  </span>
                </div>
                <div
                  className="meter"
                  style={{ background: 'var(--card)' }}
                  aria-label={`Depreciação ${(e.depreciatedBps / 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`}
                >
                  <span
                    style={{
                      display: 'block',
                      height: '100%',
                      width: `${Math.max(0.5, Math.min(100, e.depreciatedBps / 100))}%`,
                      background: 'var(--pe)',
                    }}
                  />
                </div>
                <div className="kv sm">
                  <span className="muted">Falta depreciar</span>
                  <span className="v">
                    {formatBRL(e.remainingCentavos)} ·{' '}
                    {h(Math.max(0, p.lifetimeHours - p.hoursPrinted))}
                  </span>
                </div>
              </div>
              <Installments equipment={e} onDialog={onDialog} />
              <p className="xs muted">
                Parcela é caixa, depreciação é custo. Os dois nunca são somados no lucro.
              </p>
            </section>
          );
        })}
      </div>
      <aside className="col" style={{ flex: '2 1 340px', minWidth: 0, gap: 18 }}>
        {others.map((e) => (
          <section key={e.id} className="card col" style={{ gap: 8 }}>
            <div className="row between">
              <h3>{e.name}</h3>
              <span className="tab" style={{ fontWeight: 600 }}>
                {formatBRL(e.valueCentavos)}
              </span>
            </div>
            <div className="kv sm">
              <span className="muted">Vida útil</span>
              <span className="v">{e.lifeMonths} meses</span>
            </div>
            <div className="kv sm">
              <span className="muted">Custo fixo por mês</span>
              <span className="v">{formatBRL(e.monthlyCostCentavos ?? 0)}</span>
            </div>
            <div className="kv sm">
              <span className="muted">Depreciado</span>
              <span className="v">{formatBRL(e.depreciatedCentavos)}</span>
            </div>
            {e.installments.length ? null : (
              <div className="kv sm">
                <span className="muted">Parcelas</span>
                <span className="v">{installmentsText(e.valueCentavos, 1, null)}</span>
              </div>
            )}
            <Installments equipment={e} onDialog={onDialog} />
            {isAdmin ? (
              <button
                type="button"
                className="btn sm g"
                style={{ alignSelf: 'flex-start', padding: 0 }}
                onClick={() => onDialog({ kind: 'equipment', equipment: e })}
              >
                Editar equipamento
              </button>
            ) : null}
          </section>
        ))}
        <section className="card col" style={{ gap: 6 }}>
          <h3>Equipamento ou despesa?</h3>
          <p className="sm muted">
            Comprou algo que dura mais de um ano (impressora, secador, ferramenta)? Lance como
            equipamento: o valor é diluído na vida útil e o mês não fica no vermelho de uma vez.
          </p>
        </section>
      </aside>
    </div>
  );
}
